const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;
const roundWeight = (value) => Math.round((Number(value) || 0) * 10) / 10;

function toNumber(value) {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
}

function money(value) {
  return `${roundMoney(value).toLocaleString("de-DE")} EUR`;
}

function findTariffType(tariff, tariffTypeId) {
  return (tariff.tariffTypes || []).find((type) => type.id === tariffTypeId);
}

function findZone(tariffType, recipientPostalCode) {
  const postalCode = String(recipientPostalCode || "").trim();
  const prefixes = Object.keys(tariffType.zonesByPostalPrefix || {}).sort((left, right) => right.length - left.length);
  const prefix = prefixes.find((item) => postalCode.startsWith(item));

  if (!prefix) {
    return null;
  }

  return { zone: tariffType.zonesByPostalPrefix[prefix], postalPrefix: prefix };
}

function getWeightBreak(tariffType, chargeableWeightKg) {
  const breaks = [...(tariffType.weightBreaksKg || [])].sort((left, right) => left - right);
  return breaks.find((weightBreak) => weightBreak >= chargeableWeightKg) || null;
}

function getRowNumber(row, keys) {
  const key = keys.find((candidate) => row[candidate] !== undefined && row[candidate] !== "");
  return key ? toNumber(row[key]) : 0;
}

function findInsurancePremium(insuranceTable, goodsValue, tariff) {
  const value = Math.max(0, toNumber(goodsValue));
  const isEurope = [tariff?.country, tariff?.relation].some((item) => {
    const text = String(item || "").toUpperCase();
    return text && text !== "DE" && !text.includes("DEUTSCH");
  });
  const rows = [...(insuranceTable || [])]
    .map((row) => ({
      goodsValue: getRowNumber(row, ["goodsValue", "Warenwert", "Warenwert_EUR", "Warenwert_bis_EUR", "Bis_Warenwert_EUR"]),
      premium: isEurope
        ? getRowNumber(row, ["premiumEurope", "Praemie_Europa", "Europa", "Praemie_EUR_Europa", "Versicherung_Europa_EUR", "premium"])
        : getRowNumber(row, ["premium", "premiumNational", "Praemie", "Praemie_EUR", "National", "Versicherung_EUR"]),
    }))
    .filter((row) => row.goodsValue > 0 && row.premium >= 0)
    .sort((left, right) => left.goodsValue - right.goodsValue);

  return rows.find((row) => row.goodsValue >= value) || null;
}

function parseDistanceColumn(key) {
  const match = String(key).match(/km_(\d+)_(\d+)/i) || String(key).match(/bis_(\d+)_km/i);
  if (!match) return null;
  if (match.length === 2) return { key, from: 0, to: Number(match[1]), label: `bis ${match[1]} km` };
  return { key, from: Number(match[1]), to: Number(match[2]), label: `${match[1]}-${match[2]} km` };
}

function findTollDe(tollTable, chargeableWeightKg, distanceKm) {
  const distance = Math.max(0, toNumber(distanceKm));
  const rows = [...(tollTable || [])]
    .map((row) => ({ row, weight: getRowNumber(row, ["Gewicht_kg", "weightKg", "weight", "Gewicht"]) }))
    .filter((item) => item.weight > 0)
    .sort((left, right) => left.weight - right.weight);
  const weightRow = rows.find((item) => item.weight >= chargeableWeightKg) || rows[rows.length - 1];

  if (!weightRow || distance <= 0) return null;

  const distanceColumns = Object.keys(weightRow.row)
    .map(parseDistanceColumn)
    .filter(Boolean)
    .sort((left, right) => left.to - right.to);
  const distanceColumn = distanceColumns.find((item) => distance >= item.from && distance <= item.to) || distanceColumns[distanceColumns.length - 1];

  if (!distanceColumn) return null;

  return {
    amount: roundMoney(toNumber(weightRow.row[distanceColumn.key])),
    weightBreakKg: weightRow.weight,
    distanceClass: distanceColumn.label,
  };
}

function getInternationalTollPercent(tariff) {
  const relation = String(tariff?.country || tariff?.relation || "").toUpperCase();
  const row = (tariff?.internationalToll || []).find((item) => {
    const code = String(item.Landcode || item.country || item.countryCode || "").toUpperCase();
    return code && relation.includes(code);
  }) || (tariff?.internationalToll || [])[0];
  const percent = row ? getRowNumber(row, ["Stueckgut_Prozent", "percentage", "Prozent", "Wert"]) : 0;

  if (percent > 0) {
    return { percent, source: row?.Land || row?.label || "International" };
  }

  if (relation.includes("NL") || relation.includes("NIEDER")) {
    return { percent: 1.1, source: "NL Stueckgut ab 01.07.2026" };
  }

  return null;
}

function getDieselPercent(tariff, configured) {
  if (configured?.mode === "manual") return toNumber(configured.percentage);
  const row = (tariff?.dieselTable || [])[0];
  return row ? getRowNumber(row, ["Zuschlag_Prozent", "percentage", "Prozent", "Wert"]) : 0;
}

function specialSurcharges(input, tariff, baseFreight, chargeableWeightKg) {
  const warnings = [];
  const items = [];
  const configured = input.surcharges || {};
  const insurance = configured.transportInsurance || {};
  const toll = configured.tollAuto || {};
  const diesel = configured.dieselAuto || {};

  if (insurance.mode === "manual") {
    items.push({ id: "transportInsurance", label: "Versicherung", group: "Versicherung", calculation: "Manueller Betrag", amount: roundMoney(toNumber(insurance.amount)), overridden: true });
  } else if (insurance.mode === "auto") {
    const goodsValue = toNumber(insurance.goodsValue);
    const premiumRow = findInsurancePremium(tariff.insuranceTable, goodsValue, tariff);
    if (!premiumRow) {
      warnings.push("Warenwert ist eingetragen, aber es wurde keine passende Versicherungstabelle gefunden. Bitte Versicherung manuell erfassen.");
      items.push({ id: "transportInsurance", label: "Versicherung", group: "Versicherung", calculation: `Keine Tabelle fuer ${money(goodsValue)} Warenwert`, amount: 0, warning: true });
    } else {
      const autoAmount = roundMoney(premiumRow.premium);
      const overridden = Boolean(insurance.overrideEnabled);
      items.push({
        id: "transportInsurance",
        label: overridden ? "Versicherung manuell ueberschrieben" : "Versicherung",
        group: "Versicherung",
        calculation: `Warenwert ${money(goodsValue)} -> Stufe ${money(premiumRow.goodsValue)}`,
        amount: overridden ? roundMoney(toNumber(insurance.overrideAmount)) : autoAmount,
        autoAmount,
        usedGoodsValueBreak: premiumRow.goodsValue,
        goodsValue,
        overridden,
      });
    }
  }

  if (toll.mode === "manual") {
    items.push({ id: "tollManual", label: "Maut manuell", group: "Maut Deutschland", calculation: "Manueller Betrag", amount: roundMoney(toNumber(toll.amount)), overridden: true });
  } else if (toll.mode === "auto") {
    const distanceKm = toNumber(toll.distanceKm);
    if (distanceKm <= 0) {
      warnings.push("Bitte Entfernung eingeben oder Maut manuell erfassen.");
    }
    const deToll = findTollDe(tariff.toll, chargeableWeightKg, distanceKm);
    if (deToll) {
      const overridden = Boolean(toll.overrideEnabled);
      items.push({
        id: "tollDe",
        label: overridden ? "Maut Deutschland manuell ueberschrieben" : "Maut Deutschland",
        group: "Maut Deutschland",
        calculation: `${deToll.distanceClass}, ${deToll.weightBreakKg} kg`,
        amount: overridden ? roundMoney(toNumber(toll.overrideAmount)) : deToll.amount,
        autoAmount: deToll.amount,
        distanceClass: deToll.distanceClass,
        overridden,
      });
    } else if (distanceKm > 0) {
      warnings.push("Keine passende deutsche Mauttabelle gefunden. Bitte Maut manuell erfassen.");
    }

    const intl = getInternationalTollPercent(tariff);
    if (intl && intl.percent > 0) {
      items.push({
        id: "tollInternational",
        label: "Maut International",
        group: "Maut International",
        calculation: `${money(baseFreight)} x ${intl.percent}% (${intl.source})`,
        amount: roundMoney(baseFreight * intl.percent / 100),
        percentage: intl.percent,
      });
    }
  }

  if (diesel.mode === "manual" || diesel.mode === "auto") {
    const percent = getDieselPercent(tariff, diesel);
    if (percent) {
      const autoAmount = roundMoney(baseFreight * percent / 100);
      const overridden = Boolean(diesel.overrideEnabled);
      items.push({
        id: "dieselAuto",
        label: overridden ? "Dieselzuschlag manuell ueberschrieben" : "Dieselzuschlag",
        group: "Dieselzuschlag",
        calculation: `${money(baseFreight)} x ${percent}%`,
        amount: overridden ? roundMoney(toNumber(diesel.overrideAmount)) : autoAmount,
        autoAmount,
        percentage: percent,
        overridden,
      });
    } else if (diesel.mode === "auto") {
      warnings.push("Kein aktiver Dieselzuschlag gefunden.");
    }
  }

  return { items, warnings };
}

function calculateGenericSurcharges(input, tariff, baseFreight, subtotalBeforeCapital) {
  const configured = input.surcharges || {};
  const definitions = (tariff.surchargeDefinitions || []).filter((definition) => !["transportInsurance", "manualToll", "manualDiesel", "dieselAuto", "tollAuto"].includes(definition.id));

  return definitions.map((definition) => {
    const value = configured[definition.id];
    if (!value?.enabled) return null;

    if (definition.type === "percentageOfBaseFreight") {
      const percentage = Math.max(0, toNumber(value.percentage || definition.defaultPercentage));
      return { id: definition.id, label: definition.label, group: definition.label, calculation: `${money(baseFreight)} x ${percentage}%`, amount: roundMoney(baseFreight * percentage / 100) };
    }

    if (definition.type === "percentageOfSubtotal") {
      const percentage = Math.max(0, toNumber(value.percentage || definition.defaultPercentage));
      return { id: definition.id, label: definition.label, group: definition.label, calculation: `${money(subtotalBeforeCapital)} x ${percentage}%`, amount: roundMoney(subtotalBeforeCapital * percentage / 100) };
    }

    return { id: definition.id, label: definition.label, group: "Sonstige Zuschlaege", calculation: "Fixbetrag", amount: roundMoney(Math.max(0, toNumber(value.amount || definition.defaultAmount))) };
  }).filter(Boolean);
}

export function calculateFreightPrice(input, tariff) {
  const errors = [];
  const tariffType = findTariffType(tariff, input.tariffTypeId);
  const packageDefinition = tariff.packageMinimumWeightsKg?.[input.packageTypeId];
  const palletCount = Math.max(0, toNumber(input.palletCount));
  const lengthCm = Math.max(0, toNumber(input.lengthCm));
  const widthCm = Math.max(0, toNumber(input.widthCm));
  const heightCm = Math.max(0, toNumber(input.heightCm));
  const actualWeightKg = Math.max(0, toNumber(input.actualWeightKg));

  if (!tariffType) errors.push("Bitte waehle eine gueltige Tarifart aus.");
  if (!packageDefinition) errors.push("Bitte waehle eine Packmittelart aus.");
  if (!input.recipientPostalCode) errors.push("Bitte gib eine Empfaenger-PLZ ein.");
  if (palletCount <= 0) errors.push("Bitte gib mindestens eine Palette an.");
  if (lengthCm <= 0 || widthCm <= 0 || heightCm <= 0) errors.push("Bitte gib Laenge, Breite und Hoehe groesser 0 ein.");
  if (actualWeightKg <= 0) errors.push("Bitte gib das tatsaechliche Gesamtgewicht ein.");
  if (errors.length > 0) return { ok: false, errors };

  const zoneResult = findZone(tariffType, input.recipientPostalCode);
  if (!zoneResult) return { ok: false, errors: [`Fuer die Empfaenger-PLZ ${input.recipientPostalCode} wurde keine Zone gefunden.`] };

  const cbmPerPallet = (lengthCm * widthCm * heightCm) / 1000000;
  const totalCbm = cbmPerPallet * palletCount;
  const volumetricWeightKg = totalCbm * tariff.volumetricWeightFactorKgPerCbm;
  const packageMinimumWeightKg = palletCount * Number(packageDefinition.minimumWeightKg || 0);
  const chargeableWeightKg = Math.max(actualWeightKg, volumetricWeightKg, packageMinimumWeightKg);
  const weightBreakKg = getWeightBreak(tariffType, chargeableWeightKg);

  if (!weightBreakKg) {
    return { ok: false, tariffOnRequest: true, errors: ["Tarif auf Anfrage: Das frachtpflichtige Gewicht liegt ueber der hoechsten Tabelle."], breakdown: { actualWeightKg: roundWeight(actualWeightKg), volumetricWeightKg: roundWeight(volumetricWeightKg), packageMinimumWeightKg: roundWeight(packageMinimumWeightKg), chargeableWeightKg: roundWeight(chargeableWeightKg), totalCbm: Math.round(totalCbm * 1000) / 1000, zone: zoneResult.zone } };
  }

  const priceRow = tariffType.pricesByWeightAndZone?.[weightBreakKg] || {};
  const baseFreight = priceRow[zoneResult.zone];
  if (baseFreight === null || baseFreight === undefined) {
    return { ok: false, tariffOnRequest: true, errors: [`Tarif auf Anfrage: Fuer Zone ${zoneResult.zone} und Gewichtsstufe ${weightBreakKg} kg ist kein Preis gepflegt.`], breakdown: { actualWeightKg: roundWeight(actualWeightKg), volumetricWeightKg: roundWeight(volumetricWeightKg), packageMinimumWeightKg: roundWeight(packageMinimumWeightKg), chargeableWeightKg: roundWeight(chargeableWeightKg), totalCbm: Math.round(totalCbm * 1000) / 1000, zone: zoneResult.zone, weightBreakKg } };
  }

  const special = specialSurcharges(input, tariff, baseFreight, chargeableWeightKg);
  const genericBeforeCapital = calculateGenericSurcharges({ ...input, surcharges: Object.fromEntries(Object.entries(input.surcharges || {}).filter(([key]) => key !== "capitalProvision")) }, tariff, baseFreight, baseFreight);
  const subtotalBeforeCapital = roundMoney(baseFreight + [...special.items, ...genericBeforeCapital].reduce((sum, item) => sum + item.amount, 0));
  const capitalSurcharge = calculateGenericSurcharges({ ...input, surcharges: { capitalProvision: input.surcharges?.capitalProvision } }, tariff, baseFreight, subtotalBeforeCapital);
  const surcharges = [...special.items, ...genericBeforeCapital, ...capitalSurcharge];
  const netTotal = roundMoney(baseFreight + surcharges.reduce((sum, item) => sum + item.amount, 0));
  const vatAmount = roundMoney(netTotal * Number(tariff.vatRate || 0));
  const grossTotal = roundMoney(netTotal + vatAmount);

  return {
    ok: true,
    tariffOnRequest: false,
    errors: [],
    warnings: special.warnings,
    tariff: { carrierName: tariff.carrierName, tariffTypeLabel: tariffType.label, sourceNote: tariff.sourceNote },
    breakdown: { senderPostalCode: input.senderPostalCode, recipientPostalCode: input.recipientPostalCode, postalPrefix: zoneResult.postalPrefix, zone: zoneResult.zone, cbmPerPallet: Math.round(cbmPerPallet * 1000) / 1000, totalCbm: Math.round(totalCbm * 1000) / 1000, actualWeightKg: roundWeight(actualWeightKg), volumetricWeightKg: roundWeight(volumetricWeightKg), packageMinimumWeightKg: roundWeight(packageMinimumWeightKg), chargeableWeightKg: roundWeight(chargeableWeightKg), weightBreakKg, packageMinimumWeightPerUnitKg: packageDefinition.minimumWeightKg, packageTypeLabel: packageDefinition.label, palletCount, goodsValue: toNumber(input.surcharges?.transportInsurance?.goodsValue), distanceKm: toNumber(input.surcharges?.tollAuto?.distanceKm) },
    pricing: { baseFreight: roundMoney(baseFreight), surcharges, netTotal, vatRate: tariff.vatRate, vatAmount, grossTotal },
  };
}
